<template>
  <div class="spotlight-plan-slideout">
    <div class="p-6">
      <h2 class="text-2xl font-bold mb-6">
        <i class="fas fa-lightbulb text-yellow-500 mr-2" />
        Spotlight Implementation Plan
      </h2>

      <!-- Loading State -->
      <div
        v-if="loading"
        class="text-center py-12">
        <i class="fas fa-spinner fa-spin text-3xl text-indigo-600 mb-4" />
        <p class="text-gray-600">
          Loading plan status...
        </p>
      </div>

      <!-- No Plan / Generate Button -->
      <div
        v-else-if="!plan || plan.status === 'pending'"
        class="text-center">
        <div
          v-if="!generating"
          class="bg-gray-50 rounded-lg p-8">
          <i class="fas fa-rocket text-5xl text-indigo-600 mb-4" />
          <h3 class="text-xl font-semibold mb-2">
            Generate Implementation Plan
          </h3>
          <p class="text-gray-600 mb-6">
            Analyze the website and generate recommendations for Job Site Checkins implementation.
          </p>
          <button
            class="btn btn-primary"
            :disabled="generating"
            @click="generatePlan">
            <i class="fas fa-play mr-2" />
            Generate Spotlight Implementation Plan
          </button>
        </div>

        <!-- Generation Progress -->
        <div
          v-else
          class="space-y-4">
          <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div class="flex items-center">
              <i class="fas fa-spinner fa-spin text-blue-600 mr-3" />
              <div>
                <p class="font-semibold text-blue-900">
                  {{ progressMessage }}
                </p>
                <p
                  v-if="progressDetails"
                  class="text-sm text-blue-700">
                  {{ progressDetails }}
                </p>
              </div>
            </div>
          </div>

          <!-- Manual URL Entry (if sitemap not found) -->
          <div
            v-if="showManualUrlEntry"
            class="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
            <h4 class="font-semibold text-yellow-900 mb-2">
              <i class="fas fa-exclamation-triangle mr-2" />
              Sitemap Not Found
            </h4>
            <p class="text-sm text-yellow-800 mb-4">
              We couldn't find a sitemap for this website. Please enter the URLs manually:
            </p>
            <textarea
              v-model="manualUrls"
              class="w-full h-32 px-3 py-2 border border-gray-300 rounded-md"
              placeholder="Enter URLs (one per line)&#10;https://example.com/services&#10;https://example.com/locations" />
            <button
              class="btn btn-primary mt-3"
              :disabled="!manualUrls.trim()"
              @click="submitManualUrls">
              <i class="fas fa-check mr-2" />
              Continue with Manual URLs
            </button>
          </div>
        </div>
      </div>

      <!-- Plan Display -->
      <div
        v-else-if="plan && plan.status === 'completed'"
        class="space-y-6">
        <!-- Executive Summary -->
        <div class="bg-white border border-gray-200 rounded-lg p-6">
          <h3 class="text-lg font-semibold mb-3">
            <i class="fas fa-file-alt text-gray-600 mr-2" />
            Executive Summary
          </h3>
          <div class="prose max-w-none text-gray-700">
            {{ plan.executiveSummary }}
          </div>
        </div>

        <!-- Job Categories -->
        <div
          v-if="plan.jobCategories && plan.jobCategories.length > 0"
          class="bg-white border border-gray-200 rounded-lg p-6">
          <h3 class="text-lg font-semibold mb-3">
            <i class="fas fa-th-large text-gray-600 mr-2" />
            Recommended Job Categories
          </h3>
          <div class="grid gap-3">
            <div
              v-for="(category, index) in plan.jobCategories"
              :key="index"
              class="flex items-start p-3 bg-gray-50 rounded-lg">
              <span
                class="flex-shrink-0 w-8 h-8 bg-indigo-100 text-indigo-600 rounded-full
                           flex items-center justify-center font-semibold text-sm mr-3">
                {{ index + 1 }}
              </span>
              <div class="flex-1">
                <h4 class="font-semibold text-gray-900">
                  {{ category.name }}
                </h4>
                <p class="text-sm text-gray-600 mt-1">
                  {{ category.description }}
                </p>
                <span
                  :class="getSearchVolumeClass(category.searchVolume)"
                  class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium mt-2">
                  <i class="fas fa-chart-line mr-1" />
                  {{ category.searchVolume }} search volume
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Target Pages -->
        <div
          v-if="plan.targetPages && plan.targetPages.mappings"
          class="bg-white border border-gray-200 rounded-lg p-6">
          <h3 class="text-lg font-semibold mb-3">
            <i class="fas fa-bullseye text-gray-600 mr-2" />
            Target Pages for Implementation
          </h3>
          <div class="space-y-3">
            <div
              v-for="mapping in plan.targetPages.mappings"
              :key="mapping.jobCategory"
              class="border-l-4 border-indigo-400 pl-4 py-2">
              <h4 class="font-semibold text-gray-900">
                {{ mapping.jobCategory }}
              </h4>
              <a
                :href="mapping.targetUrl"
                target="_blank"
                class="text-sm text-blue-600 hover:underline flex items-center mt-1">
                {{ mapping.targetUrl }}
                <i class="fas fa-external-link-alt ml-1 text-xs" />
              </a>
              <p class="text-sm text-gray-600 mt-1">
                {{ mapping.reason }}
              </p>
            </div>
          </div>
        </div>

        <!-- Missing Pages -->
        <div
          v-if="plan.missingPages && plan.missingPages.length > 0"
          class="bg-white border border-gray-200 rounded-lg p-6">
          <h3 class="text-lg font-semibold mb-3">
            <i class="fas fa-exclamation-circle text-yellow-600 mr-2" />
            Recommended New Pages
          </h3>
          <ul class="space-y-2">
            <li
              v-for="(page, index) in plan.missingPages"
              :key="index"
              class="flex items-start">
              <i class="fas fa-plus-circle text-green-500 mr-2 mt-0.5" />
              <span class="text-gray-700">{{ page }}</span>
            </li>
          </ul>
        </div>

        <!-- Actions -->
        <div class="flex justify-end space-x-3 pt-4">
          <button
            class="btn btn-secondary"
            @click="copyToClipboard">
            <i class="fas fa-copy mr-2" />
            Copy Plan
          </button>
          <button
            class="btn btn-primary"
            @click="regeneratePlan">
            <i class="fas fa-sync mr-2" />
            Regenerate Plan
          </button>
        </div>

        <!-- Discovered Pages Table -->
        <div
          v-if="plan.websiteProfileId"
          class="mt-8 bg-white border border-gray-200 rounded-lg p-6">
          <h3 class="text-lg font-semibold mb-4">
            <i class="fas fa-globe text-gray-600 mr-2" />
            Discovered Website Pages
          </h3>

          <!-- Search Input -->
          <div class="mb-4">
            <div class="relative">
              <input
                v-model="pageSearch"
                type="text"
                class="w-full px-3 py-2 pl-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="Search pages by URL or title...">
              <i class="fas fa-search absolute left-3 top-3 text-gray-400" />
            </div>
          </div>

          <!-- Loading indicator for pages -->
          <div
            v-if="loadingPages"
            class="text-center py-8">
            <i class="fas fa-spinner fa-spin text-2xl text-indigo-600 mb-2" />
            <p class="text-sm text-gray-600">
              Loading discovered pages...
            </p>
          </div>

          <!-- Table -->
          <div
            v-else
            class="overflow-x-auto">
            <table class="min-w-full divide-y divide-gray-200">
              <thead class="bg-gray-50">
                <tr>
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    URL
                  </th>
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Title
                  </th>
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Category
                  </th>
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Crawled
                  </th>
                </tr>
              </thead>
              <tbody class="bg-white divide-y divide-gray-200">
                <tr
                  v-for="page in paginatedPages"
                  :key="page.id"
                  class="hover:bg-gray-50">
                  <td class="px-4 py-3 text-sm">
                    <a
                      :href="page.url"
                      target="_blank"
                      class="text-indigo-600 hover:text-indigo-800 hover:underline">
                      {{ truncateUrl(page.url) }}
                      <i class="fas fa-external-link-alt text-xs ml-1" />
                    </a>
                  </td>
                  <td class="px-4 py-3 text-sm text-gray-900">
                    {{ page.title || 'No title' }}
                  </td>
                  <td class="px-4 py-3 text-sm">
                    <span
                      v-if="page.category"
                      class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium"
                      :class="getCategoryClass(page.category)">
                      {{ page.category }}
                    </span>
                    <span
                      v-else
                      class="text-gray-400">
                      Uncategorized
                    </span>
                  </td>
                  <td class="px-4 py-3 text-sm text-gray-500">
                    {{ formatDate(page.crawledAt) }}
                  </td>
                </tr>
              </tbody>
            </table>

            <!-- Empty State -->
            <div
              v-if="websitePages.length === 0"
              class="text-center py-8">
              <i class="fas fa-globe text-3xl text-gray-300 mb-2" />
              <p class="text-sm text-gray-500">
                No website pages discovered yet
              </p>
            </div>
          </div>

          <!-- Pagination -->
          <div
            v-if="websitePages.length > 0"
            class="mt-4 flex items-center justify-between">
            <div class="text-sm text-gray-700">
              Showing {{ (currentPage - 1) * pageSize + 1 }} to {{ Math.min(currentPage * pageSize, filteredPages.length) }}
              of {{ filteredPages.length }} pages
            </div>
            <div class="flex space-x-2">
              <button
                :disabled="currentPage === 1"
                class="px-3 py-1 text-sm rounded border"
                :class="currentPage === 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50'"
                @click="currentPage--">
                <i class="fas fa-chevron-left" />
              </button>
              <span class="px-3 py-1 text-sm">
                Page {{ currentPage }} of {{ totalPages }}
              </span>
              <button
                :disabled="currentPage === totalPages"
                class="px-3 py-1 text-sm rounded border"
                :class="currentPage === totalPages ?
                  'bg-gray-100 text-gray-400 cursor-not-allowed' :
                  'bg-white text-gray-700 hover:bg-gray-50'"
                @click="currentPage++">
                <i class="fas fa-chevron-right" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Error State -->
      <div
        v-else-if="plan && plan.status === 'failed'"
        class="bg-red-50 border border-red-200 rounded-lg p-6">
        <h3 class="text-lg font-semibold text-red-900 mb-2">
          <i class="fas fa-times-circle mr-2" />
          Generation Failed
        </h3>
        <p class="text-red-700">
          Failed to generate the implementation plan. Please try again.
        </p>
        <button
          class="btn btn-primary mt-4"
          @click="generatePlan">
          <i class="fas fa-redo mr-2" />
          Try Again
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import {
  ref, onMounted, onUnmounted, computed, watch
} from 'vue'
import { createConsumer } from '@rails/actioncable'
import axios from '@/init/axios'

export default {
  name: 'SpotlightImplementationPlanSlideout',
  props: {
    organizationId: {
      type: [String, Number],
      required: true
    }
  },
  emits: ['planGenerated'],
  setup(props, { emit }) {
    // State
    const loading = ref(true)
    const plan = ref(null)
    const generating = ref(false)
    const progressMessage = ref('')
    const progressDetails = ref('')
    const showManualUrlEntry = ref(false)
    const manualUrls = ref('')
    const error = ref(null)

    // Discovered Pages Table State
    const websitePages = ref([])
    const pageSearch = ref('')
    const currentPage = ref(1)
    const pageSize = ref(10)
    const loadingPages = ref(false)

    // ActionCable
    let subscription = null
    const consumer = createConsumer()

    // Computed
    const getSearchVolumeClass = computed(() => (volume) => {
      const classes = {
        high: 'bg-green-100 text-green-800',
        medium: 'bg-yellow-100 text-yellow-800',
        low: 'bg-gray-100 text-gray-800'
      }
      return classes[volume] || classes.low
    })

    // Computed for pages table
    const filteredPages = computed(() => {
      if (!pageSearch.value) return websitePages.value

      const search = pageSearch.value.toLowerCase()
      return websitePages.value.filter((page) => page.url.toLowerCase().includes(search) ||
        (page.title && page.title.toLowerCase().includes(search)) ||
        (page.category && page.category.toLowerCase().includes(search)))
    })

    const totalPages = computed(() => Math.max(1, Math.ceil(filteredPages.value.length / pageSize.value)))

    const paginatedPages = computed(() => {
      const start = (currentPage.value - 1) * pageSize.value
      const end = start + pageSize.value
      return filteredPages.value.slice(start, end)
    })

    // Function declarations (hoisted)
    // Load website pages when plan is loaded
    const loadWebsitePages = async() => {
      if (!plan.value || !plan.value.websiteProfileId) {
        // eslint-disable-next-line no-console
        console.log('No websiteProfileId available:', plan.value)
        return
      }

      try {
        loadingPages.value = true
        const response = await axios.get(`/ajax/website_profiles/${plan.value.websiteProfileId}/pages`)
        websitePages.value = response.data.pages || []
        // Reset to first page when loading new data
        currentPage.value = 1
        // eslint-disable-next-line no-console
        console.log('Loaded pages:', websitePages.value.length)
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to load website pages:', err)
      } finally {
        loadingPages.value = false
      }
    }

    const loadLatestPlan = async() => {
      try {
        loading.value = true
        const response = await axios.get(`/ajax/organizations/${props.organizationId}/spotlight_implementation_plans/latest`)
        plan.value = response.data.plan

        // Load website pages if plan exists
        if (plan.value && plan.value.status === 'completed') {
          await loadWebsitePages()
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to load plan:', err)
        error.value = 'Failed to load plan'
      } finally {
        loading.value = false
      }
    }

    const handleProgressUpdate = (data) => {
      switch (data.event) {
        case 'discovery_started':
          progressMessage.value = 'Discovering website sitemap...'
          break
        case 'discovery_failed':
          progressMessage.value = 'Sitemap not found'
          showManualUrlEntry.value = true
          // Load the plan that was created
          loadLatestPlan()
          break
        case 'crawling_pages':
          progressMessage.value = 'Crawling website pages...'
          break
        case 'pages_found':
          progressMessage.value = 'Found website pages'
          progressDetails.value = `${data.count} pages discovered`
          break
        case 'categorizing_pages':
          progressMessage.value = 'Categorizing pages with AI...'
          progressDetails.value = 'This may take a few minutes'
          break
        case 'categorization_complete':
          progressMessage.value = 'Page categorization complete'
          break
        case 'generating_job_categories':
          progressMessage.value = 'Generating job categories...'
          break
        case 'generating_plan':
          progressMessage.value = 'Creating implementation plan...'
          break
        case 'plan_generated':
          progressMessage.value = 'Plan generated successfully!'
          generating.value = false
          loadLatestPlan().then(() => {
            loadWebsitePages()
          })
          emit('planGenerated')
          break
        case 'error':
        case 'crawl_failed':
        case 'job_categories_failed':
        case 'plan_generation_failed':
          progressMessage.value = 'Generation failed'
          progressDetails.value = data.error || 'An error occurred'
          generating.value = false
          loadLatestPlan()
          break
        default:
          // eslint-disable-next-line no-console
          console.warn('Unknown progress event:', data.event)
      }
    }

    const subscribeToProgress = () => {
      if (subscription) return

      subscription = consumer.subscriptions.create(
        {
          channel: 'SpotlightImplementationPlanChannel',
          organization_id: props.organizationId
        },
        {
          connected() {
            // eslint-disable-next-line no-console
            console.log('Connected to SpotlightImplementationPlanChannel')
          },
          disconnected() {
            // eslint-disable-next-line no-console
            console.log('Disconnected from SpotlightImplementationPlanChannel')
          },
          received(data) {
            handleProgressUpdate(data)
          }
        }
      )
    }

    const formatPlanAsText = (planData) => {
      let text = 'SPOTLIGHT IMPLEMENTATION PLAN\n\n'

      text += 'EXECUTIVE SUMMARY\n'
      text += `${planData.executiveSummary}\n\n`

      text += 'RECOMMENDED JOB CATEGORIES\n'
      planData.jobCategories.forEach((cat, i) => {
        text += `${i + 1}. ${cat.name}\n`
        text += `   ${cat.description}\n`
        text += `   Search Volume: ${cat.searchVolume}\n\n`
      })

      text += 'TARGET PAGES\n'
      planData.targetPages.mappings.forEach((mapping) => {
        text += `- ${mapping.jobCategory}\n`
        text += `  Target: ${mapping.targetUrl}\n`
        text += `  Reason: ${mapping.reason}\n\n`
      })

      if (planData.missingPages && planData.missingPages.length > 0) {
        text += 'RECOMMENDED NEW PAGES\n'
        planData.missingPages.forEach((page) => {
          text += `- ${page}\n`
        })
      }

      return text
    }

    // Methods
    const generatePlan = async() => {
      try {
        generating.value = true
        progressMessage.value = 'Starting plan generation...'
        progressDetails.value = ''
        showManualUrlEntry.value = false

        await axios.post(`/ajax/organizations/${props.organizationId}/spotlight_implementation_plans`)

        // Subscribe to ActionCable for progress updates
        subscribeToProgress()
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to start generation:', err)
        generating.value = false
        error.value = err.response?.data?.error || 'Failed to start generation'
      }
    }

    const regeneratePlan = () => {
      plan.value = null
      generatePlan()
    }

    const submitManualUrls = async() => {
      if (!plan.value || !manualUrls.value.trim()) return

      try {
        showManualUrlEntry.value = false
        progressMessage.value = 'Processing manual URLs...'

        await axios.post(
          `/ajax/organizations/${props.organizationId}/spotlight_implementation_plans/${plan.value.id}/submit_manual_urls`,
          { urls: manualUrls.value }
        )
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to submit manual URLs:', err)
        error.value = 'Failed to submit URLs'
        showManualUrlEntry.value = true
      }
    }

    const copyToClipboard = () => {
      if (!plan.value) return

      const text = formatPlanAsText(plan.value)
      navigator.clipboard.writeText(text).then(() => {
        // Show success message
        progressMessage.value = 'Plan copied to clipboard!'
        setTimeout(() => {
          progressMessage.value = ''
        }, 3000)
      })
    }

    // Helper methods for pages table
    const truncateUrl = (url) => {
      const maxLength = 60
      if (url.length <= maxLength) return url
      return `${url.substring(0, maxLength)}...`
    }

    const getCategoryClass = (category) => {
      const categoryClasses = {
        services: 'bg-blue-100 text-blue-800',
        locations: 'bg-green-100 text-green-800',
        about: 'bg-purple-100 text-purple-800',
        contact: 'bg-yellow-100 text-yellow-800',
        blog: 'bg-pink-100 text-pink-800',
        careers: 'bg-indigo-100 text-indigo-800',
        other: 'bg-gray-100 text-gray-800'
      }
      return categoryClasses[category] || categoryClasses.other
    }

    const formatDate = (dateString) => {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return `${date.toLocaleDateString()} ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
    }

    // Watchers
    watch(pageSearch, () => {
      currentPage.value = 1
    })

    // Lifecycle
    onMounted(() => {
      loadLatestPlan()
    })

    onUnmounted(() => {
      if (subscription) {
        subscription.unsubscribe()
      }
    })

    return {
      loading,
      plan,
      generating,
      progressMessage,
      progressDetails,
      showManualUrlEntry,
      manualUrls,
      error,
      loadLatestPlan,
      generatePlan,
      regeneratePlan,
      submitManualUrls,
      copyToClipboard,
      getSearchVolumeClass,
      // Pages table
      websitePages,
      pageSearch,
      currentPage,
      pageSize,
      loadingPages,
      filteredPages,
      totalPages,
      paginatedPages,
      truncateUrl,
      getCategoryClass,
      formatDate
    }
  }
}
</script>

<style scoped>
.spotlight-plan-slideout {
  height: 100%;
  overflow-y: auto;
}

.btn {
  @apply inline-flex items-center px-4 py-2 border rounded-md font-medium text-sm
         focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors;
}

.btn-primary {
  @apply border-transparent bg-indigo-600 text-white hover:bg-indigo-700
         focus:ring-indigo-500;
}

.btn-secondary {
  @apply border-gray-300 bg-white text-gray-700 hover:bg-gray-50
         focus:ring-indigo-500;
}

.btn:disabled {
  @apply opacity-50 cursor-not-allowed;
}

.prose {
  @apply text-gray-700 leading-relaxed;
}

.prose p {
  @apply mb-4;
}
</style>
